// CustomerSitesCard — full CRUD on customer_sites for the
// SkipCustomerDetailPage. Wraps the four hooks
// (useCustomerSites/useCreateCustomerSite/useUpdateCustomerSite/
// useDeleteCustomerSite) into one self-contained card.
//
// Server contract:
//   GET    /api/v1/customers/:enc_id/sites?include_inactive=1
//   POST   /api/v1/customers/:enc_id/sites
//   PATCH  /api/v1/customers/:enc_id/sites/:site_enc_id
//   DELETE /api/v1/customers/:enc_id/sites/:site_enc_id
//
// is_primary promotion goes through the PATCH endpoint with
// `{ is_primary: true }`; the server demotes other primaries in the
// same transaction. Deletion of a site that has accumulated job
// history surfaces a 409 — the row stays visible with a hint that
// the user can deactivate it instead.
import { useState, type KeyboardEvent } from 'react';

import { Button } from '../../../../components/ui/Button/Button';
import { Card } from '../../../../components/ui/Card/Card';
import {
  useCreateCustomerSite,
  useCustomerSites,
  useDeleteCustomerSite,
  useUpdateCustomerSite,
  type UpdateCustomerSiteInput,
} from '../../hooks/useSkipJobs';
import type { CustomerSite } from '../../../../types/api';
import { extractErrorMessage } from '../../../../utils/errorMessage';

import styles from './CustomerSitesCard.module.css';

export interface CustomerSitesCardProps {
  customerEncId: string;
}

interface AddFormState {
  label: string;
  address_line1: string;
  address_line2: string;
  city: string;
  postcode: string;
}

const EMPTY_ADD_FORM: AddFormState = {
  label: '',
  address_line1: '',
  address_line2: '',
  city: '',
  postcode: '',
};

export function CustomerSitesCard({ customerEncId }: CustomerSitesCardProps): JSX.Element {
  const sitesQuery = useCustomerSites(customerEncId, { includeInactive: true });
  const createSite = useCreateCustomerSite(customerEncId);
  const updateSite = useUpdateCustomerSite(customerEncId);
  const deleteSite = useDeleteCustomerSite(customerEncId);

  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState<AddFormState>(EMPTY_ADD_FORM);
  const [addError, setAddError] = useState<string | null>(null);

  // Per-row state; null = not editing; string = the site enc_id being edited.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<AddFormState>(EMPTY_ADD_FORM);
  const [editError, setEditError] = useState<string | null>(null);
  const [rowError, setRowError] = useState<{ encId: string; message: string } | null>(null);

  const sites: CustomerSite[] = sitesQuery.data?.sites ?? [];

  function siteEncIdOf(s: CustomerSite): string {
    return (s.enc_id ?? s.id) as string;
  }

  function openEdit(s: CustomerSite): void {
    setEditingId(siteEncIdOf(s));
    setEditForm({
      label: s.site_name,
      address_line1: s.address_line1,
      address_line2: s.address_line2 ?? '',
      city: s.city ?? '',
      postcode: s.postcode,
    });
    setEditError(null);
  }

  function handleAddSubmit(): void {
    setAddError(null);
    if (!addForm.address_line1.trim() || !addForm.postcode.trim()) {
      setAddError('Address line 1 and postcode are required.');
      return;
    }
    createSite.mutate(
      {
        address_line1: addForm.address_line1.trim(),
        address_line2: addForm.address_line2.trim() || null,
        city: addForm.city.trim() || null,
        postcode: addForm.postcode.trim(),
        label: addForm.label.trim() || undefined,
      },
      {
        onSuccess: () => {
          setShowAdd(false);
          setAddForm(EMPTY_ADD_FORM);
        },
        onError: (err) => setAddError(extractErrorMessage(err)),
      },
    );
  }

  function handleEditSubmit(siteEncId: string): void {
    setEditError(null);
    if (!editForm.address_line1.trim() || !editForm.postcode.trim()) {
      setEditError('Address line 1 and postcode are required.');
      return;
    }
    const patch: UpdateCustomerSiteInput = {
      label: editForm.label.trim() || undefined,
      address_line1: editForm.address_line1.trim(),
      address_line2: editForm.address_line2.trim() || null,
      city: editForm.city.trim() || null,
      postcode: editForm.postcode.trim(),
    };
    updateSite.mutate(
      { siteEncId, patch },
      {
        onSuccess: () => {
          setEditingId(null);
          setEditForm(EMPTY_ADD_FORM);
        },
        onError: (err) => setEditError(extractErrorMessage(err)),
      },
    );
  }

  function handleSetPrimary(siteEncId: string): void {
    setRowError(null);
    updateSite.mutate(
      { siteEncId, patch: { is_primary: true } },
      {
        onError: (err) =>
          setRowError({ encId: siteEncId, message: extractErrorMessage(err) }),
      },
    );
  }

  function handleToggleActive(s: CustomerSite): void {
    const siteEncId = siteEncIdOf(s);
    setRowError(null);
    updateSite.mutate(
      { siteEncId, patch: { is_active: !s.is_active } },
      {
        onError: (err) =>
          setRowError({ encId: siteEncId, message: extractErrorMessage(err) }),
      },
    );
  }

  function handleDelete(siteEncId: string): void {
    if (!window.confirm('Delete this site? This cannot be undone.')) return;
    setRowError(null);
    deleteSite.mutate(
      { siteEncId },
      {
        onError: (err) => {
          // Server returns 409 when the site has job history — surface
          // the message inline so the user knows to deactivate instead.
          setRowError({
            encId: siteEncId,
            message: extractErrorMessage(err),
          });
        },
      },
    );
  }

  function inputKeyHandler(onSubmit: () => void): (e: KeyboardEvent<HTMLInputElement>) => void {
    return (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        onSubmit();
      }
    };
  }

  return (
    <Card>
      <div className={styles.headerRow}>
        <h2>Sites</h2>
        {!showAdd ? (
          <Button
            variant="secondary"
            type="button"
            onClick={() => {
              setShowAdd(true);
              setAddError(null);
              setAddForm(EMPTY_ADD_FORM);
            }}
          >
            Add Site
          </Button>
        ) : null}
      </div>

      {showAdd ? (
        <div
          className={styles.editForm}
          role="group"
          aria-label="Add new site"
        >
          <label className={[styles.field, styles.editFormFull].join(' ')}>
            <span className={styles.label}>Label (optional)</span>
            <input
              className={styles.input}
              value={addForm.label}
              onChange={(e) => setAddForm({ ...addForm, label: e.target.value })}
              onKeyDown={inputKeyHandler(handleAddSubmit)}
              maxLength={255}
              placeholder="e.g. Depot, Site B"
            />
          </label>
          <label className={[styles.field, styles.editFormFull].join(' ')}>
            <span className={styles.label}>Address line 1</span>
            <input
              required
              className={styles.input}
              value={addForm.address_line1}
              onChange={(e) =>
                setAddForm({ ...addForm, address_line1: e.target.value })
              }
              onKeyDown={inputKeyHandler(handleAddSubmit)}
              maxLength={255}
            />
          </label>
          <label className={[styles.field, styles.editFormFull].join(' ')}>
            <span className={styles.label}>Address line 2 (optional)</span>
            <input
              className={styles.input}
              value={addForm.address_line2}
              onChange={(e) =>
                setAddForm({ ...addForm, address_line2: e.target.value })
              }
              onKeyDown={inputKeyHandler(handleAddSubmit)}
              maxLength={255}
            />
          </label>
          <label className={styles.field}>
            <span className={styles.label}>City (optional)</span>
            <input
              className={styles.input}
              value={addForm.city}
              onChange={(e) => setAddForm({ ...addForm, city: e.target.value })}
              onKeyDown={inputKeyHandler(handleAddSubmit)}
              maxLength={100}
            />
          </label>
          <label className={styles.field}>
            <span className={styles.label}>Postcode</span>
            <input
              required
              className={styles.input}
              value={addForm.postcode}
              onChange={(e) => setAddForm({ ...addForm, postcode: e.target.value })}
              onKeyDown={inputKeyHandler(handleAddSubmit)}
              maxLength={20}
            />
          </label>
          {addError ? (
            <p className={[styles.error, styles.editFormFull].join(' ')}>{addError}</p>
          ) : null}
          <div className={styles.editActions}>
            <Button
              variant="ghost"
              type="button"
              onClick={() => {
                setShowAdd(false);
                setAddError(null);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              loading={createSite.isPending}
              disabled={
                !addForm.address_line1.trim() || !addForm.postcode.trim()
              }
              onClick={handleAddSubmit}
            >
              Save Site
            </Button>
          </div>
        </div>
      ) : null}

      {sitesQuery.isLoading ? (
        <p className={styles.empty}>Loading sites…</p>
      ) : sites.length === 0 ? (
        <p className={styles.empty}>No saved sites yet. Click Add Site to create one.</p>
      ) : (
        <div>
          {sites.map((s) => {
            const siteEncId = siteEncIdOf(s);
            const isEditing = editingId === siteEncId;
            const error = rowError?.encId === siteEncId ? rowError.message : null;
            return (
              <div className={styles.row} key={siteEncId}>
                <div className={styles.rowHeader}>
                  <div className={styles.rowAddress}>
                    <strong>{s.address_line1}</strong>
                    <span>
                      {[s.address_line2, s.city, s.postcode]
                        .filter((p) => typeof p === 'string' && p.length > 0)
                        .join(', ')}
                    </span>
                  </div>
                  <div className={styles.rowMeta}>
                    {s.site_name && s.site_name !== s.address_line1 ? (
                      <span className={styles.badge}>{s.site_name}</span>
                    ) : null}
                    {s.is_primary ? (
                      <span className={[styles.badge, styles.badgePrimary].join(' ')}>
                        Primary
                      </span>
                    ) : null}
                    {!s.is_active ? (
                      <span className={[styles.badge, styles.badgeInactive].join(' ')}>
                        Inactive
                      </span>
                    ) : null}
                  </div>
                </div>

                {!isEditing ? (
                  <div className={styles.rowActions}>
                    <Button variant="ghost" type="button" onClick={() => openEdit(s)}>
                      Edit
                    </Button>
                    {!s.is_primary ? (
                      <Button
                        variant="ghost"
                        type="button"
                        onClick={() => handleSetPrimary(siteEncId)}
                        loading={updateSite.isPending}
                      >
                        Set as Primary
                      </Button>
                    ) : null}
                    <Button
                      variant="ghost"
                      type="button"
                      onClick={() => handleToggleActive(s)}
                      loading={updateSite.isPending}
                    >
                      {s.is_active ? 'Deactivate' : 'Reactivate'}
                    </Button>
                    <Button
                      variant="ghost"
                      type="button"
                      onClick={() => handleDelete(siteEncId)}
                      loading={deleteSite.isPending}
                    >
                      Delete
                    </Button>
                  </div>
                ) : (
                  <div
                    className={styles.editForm}
                    role="group"
                    aria-label="Edit site"
                  >
                    <label className={[styles.field, styles.editFormFull].join(' ')}>
                      <span className={styles.label}>Label</span>
                      <input
                        className={styles.input}
                        value={editForm.label}
                        onChange={(e) =>
                          setEditForm({ ...editForm, label: e.target.value })
                        }
                        onKeyDown={inputKeyHandler(() => handleEditSubmit(siteEncId))}
                        maxLength={255}
                      />
                    </label>
                    <label className={[styles.field, styles.editFormFull].join(' ')}>
                      <span className={styles.label}>Address line 1</span>
                      <input
                        required
                        className={styles.input}
                        value={editForm.address_line1}
                        onChange={(e) =>
                          setEditForm({ ...editForm, address_line1: e.target.value })
                        }
                        onKeyDown={inputKeyHandler(() => handleEditSubmit(siteEncId))}
                        maxLength={255}
                      />
                    </label>
                    <label className={[styles.field, styles.editFormFull].join(' ')}>
                      <span className={styles.label}>Address line 2 (optional)</span>
                      <input
                        className={styles.input}
                        value={editForm.address_line2}
                        onChange={(e) =>
                          setEditForm({ ...editForm, address_line2: e.target.value })
                        }
                        onKeyDown={inputKeyHandler(() => handleEditSubmit(siteEncId))}
                        maxLength={255}
                      />
                    </label>
                    <label className={styles.field}>
                      <span className={styles.label}>City (optional)</span>
                      <input
                        className={styles.input}
                        value={editForm.city}
                        onChange={(e) =>
                          setEditForm({ ...editForm, city: e.target.value })
                        }
                        onKeyDown={inputKeyHandler(() => handleEditSubmit(siteEncId))}
                        maxLength={100}
                      />
                    </label>
                    <label className={styles.field}>
                      <span className={styles.label}>Postcode</span>
                      <input
                        required
                        className={styles.input}
                        value={editForm.postcode}
                        onChange={(e) =>
                          setEditForm({ ...editForm, postcode: e.target.value })
                        }
                        onKeyDown={inputKeyHandler(() => handleEditSubmit(siteEncId))}
                        maxLength={20}
                      />
                    </label>
                    {editError ? (
                      <p className={[styles.error, styles.editFormFull].join(' ')}>
                        {editError}
                      </p>
                    ) : null}
                    <div className={styles.editActions}>
                      <Button
                        variant="ghost"
                        type="button"
                        onClick={() => {
                          setEditingId(null);
                          setEditError(null);
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        variant="primary"
                        loading={updateSite.isPending}
                        disabled={
                          !editForm.address_line1.trim() || !editForm.postcode.trim()
                        }
                        onClick={() => handleEditSubmit(siteEncId)}
                      >
                        Save Changes
                      </Button>
                    </div>
                  </div>
                )}

                {error ? <p className={styles.error}>{error}</p> : null}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
